
import { FlyoutDataI18n, FlyoutDataItem, FlyoutDataVehiclesData } from '@/lib/interfaces';
import { trackingService } from '@/lib/tracking';
import { toPascalCase } from '@/lib/GenericHelper';
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import BaseIcon from '@/components/FlyoutWrapper/components/BaseIcon/BaseIcon.vue';

@Component({
  name: 'FlyoutGroupItem',
  components: {
    BaseIcon,
  },
})
export default class FlyoutGroupItem extends Vue {
  @Prop({ required: true, type: Object }) itemData!: FlyoutDataItem;
  @Prop({ required: false, type: Boolean, default: false }) hasSublevel!: boolean;
  @Prop({ required: false, type: Boolean, default: false }) isSubItem!: boolean;
  @Prop({ required: false, type: Boolean, default: false }) hasImages!: boolean;
  @Inject('i18n') private i18n!: FlyoutDataI18n;
  @Inject('vehiclesData') private vehiclesData!: FlyoutDataVehiclesData;

  get itemName(): string {
    if (this.itemData.nameI18nKey && this.i18n.values[this.itemData.nameI18nKey])
      return this.i18n.values[this.itemData.nameI18nKey];
    else if (this.itemData.nameOverrideI18nKey && this.i18n.values[this.itemData.nameOverrideI18nKey])
      return this.i18n.values[this.itemData.nameOverrideI18nKey];
    else if (this.itemData.name) return this.itemData.name;
    else if (this.itemData.vehicleDataId && this.vehiclesData.values[this.itemData.vehicleDataId])
      return this.vehiclesData.values[this.itemData.vehicleDataId].name;
    else return 'Missing name';
  }

  get trackingLabel(): string {
    // We get the label for tracking purposes here
    // if the item is a subItem aka second level item, it means, at least for now
    // that we're in a "vehicle", and since the data is coming from the vehicleDataId "a-class:hatchback:W177"
    // we separate the ':' parts and after,
    // concatenate the value like 'bodyType.class', so in the example above we return: 'hatchback.a-class'
    if (this.itemData.nameI18nKey) {
      return this.itemData.nameI18nKey.split('.')[1];
    } else if (this.itemData.nameOverrideI18nKey) {
      return this.itemData.nameOverrideI18nKey.split('.')[1];
    } else if (this.itemData.vehicleDataId) {
      if (this.isSubItem) {
        const splitedName = this.itemData.vehicleDataId.split(':');
        return `${splitedName[1]}.${splitedName[0]}`;
      }
      return 'UNDEFINED VEHICLE DATA';
    } else {
      return 'UNDEFINED';
    }
  }

  get groupImageSrc(): string | undefined {
    if (this.trackingLabel != 'all' && this.trackingLabel != 'new') {
      return this.trackingLabel;
    }
    return undefined;
  }

  get labelClass(): string {
    // We fetch the CSS class for each label using the trackingLabel which is standardized
    return this.$style[`flyoutGroupItemLinkLabel${toPascalCase(this.trackingLabel)}`];
  }

  itemClickHandler(event: MouseEvent): void {
    const label = this.trackingLabel;
    // If the item has a subLevel, it is not a link, but we force the tracking here as well!
    if (this.hasSublevel) {
      event.preventDefault();
      this.$emit('itemClick', { subgroup: this.itemData });
    } else {
      const eventTarget = event.currentTarget as HTMLElement;
      const targetUrl = eventTarget.getAttribute('href');
      /**
       * If this is a subItem aka second level item, at the current state it is a vehicle
       * and thus, we need to track some vehicle info, and the data is available in the vehicleDataId key
       * we split it just like we did for the tracking label, but we use the parts separated like this:
       *
       *    a-class  :  hatchback  :     W177      |      mayback
       *     class      bodyType      modelSeries         discard
       *
       * If it is not a subItem, we just track the link, without the vehicle info!
       */
      if (this.isSubItem) {
        const vehicleNameSubrandSplit = this.itemData.vehicleDataId?.split('|');
        const vehicleNameParts = vehicleNameSubrandSplit && vehicleNameSubrandSplit[0].split(':');
        const trackingInfo = {
          event: 'click',
          eventDetail: {
            category: 'link',
            action: 'owc-header|owc-header',
            label: `our_models.${label}`,
            URL: targetUrl,
            vehicle: {
              class: vehicleNameParts && vehicleNameParts[0],
              bodyType: vehicleNameParts && vehicleNameParts[1],
              modelSeries: vehicleNameParts && vehicleNameParts[2],
            },
          },
        };
        trackingService.track(trackingInfo);
      } else {
        const trackingInfo = {
          event: 'click',
          eventDetail: {
            category: 'link',
            action: 'owc-header|owc-header',
            label: `our_models.${label}`,
            URL: targetUrl,
          },
        };
        trackingService.track(trackingInfo);
      }
    }
  }
}
